/** useViewables.ts
 *  Utilities for discovering & loading viewables from an APS bubble.
 *  No viewer/init concerns here—just viewables.
 */

export type ViewableInfo = {
  guid: string;
  name: string;
  role: string; // '2d' | '3d' | ''
  node: any; // bubble node to pass to viewer.loadDocumentNode(...)
};

/** --- internal helpers --- */
const kidsOf = (node: any): any[] => {
  if (!node) return [];
  try {
    if (typeof node.getChildren === 'function') return node.getChildren() || [];
    if (Array.isArray(node.children)) return node.children || [];
  } catch {}
  return [];
};

export const normGuid = (n: any, fallback: string) => {
  const d = n?.data || {};
  const raw =
    d.guid ??
    (typeof n?.guid === 'function' ? n.guid() : n?.guid) ??
    (typeof n?.getGuid === 'function' ? n.getGuid() : undefined) ??
    '';
  return String(raw || fallback);
};

export const normName = (n: any, idx = 0) => {
  const d = n?.data || {};
  const raw =
    d.name ??
    d.title ??
    (typeof n?.name === 'function' ? n.name() : n?.name) ??
    (typeof n?.getName === 'function' ? n.getName() : undefined) ??
    '';
  return String(raw || `View ${idx + 1}`);
};

export const normRole = (n: any) => {
  const d = n?.data || {};
  const raw =
    d.role ??
    (typeof n?.role === 'function' ? n.role() : n?.role) ??
    (typeof n?.getRole === 'function' ? n.getRole() : undefined) ??
    '';
  return String(raw || '');
};

/** BFS over bubble preserving natural order; grabs all geometry nodes (2D & 3D). */
export const gatherGeometryNodes = (root: any, debug = false): any[] => {
  const out: any[] = [];
  const q: any[] = [root];
  let visited = 0;

  while (q.length) {
    const node = q.shift();
    if (!node) continue;
    visited++;

    const t = node?.data?.type ?? node?.type;
    const g = node?.data?.guid ?? node?.guid;
    if (t === 'geometry' && g) out.push(node);

    for (const k of kidsOf(node)) q.push(k);
  }

  if (debug)
    console.log(
      '[useViewables] BFS visited:',
      visited,
      'geometry:',
      out.length
    );
  return out;
};

/** If we didn’t get anything via BFS, look into folders with role=viewable. */
export const gatherFromViewableFolders = (root: any, debug = false): any[] => {
  const folders: any[] = [];
  const stack = [root];
  while (stack.length) {
    const node = stack.pop();
    if (!node) continue;
    const role = (node?.data?.role ?? node?.role ?? '')
      .toString()
      .toLowerCase();
    if (role === 'viewable') folders.push(node);
    for (const k of kidsOf(node)) stack.push(k);
  }

  const out: any[] = [];
  for (const folder of folders) {
    for (const k of kidsOf(folder)) {
      const t = k?.data?.type ?? k?.type;
      if (t === 'geometry') out.push(k);
    }
  }
  if (debug)
    console.log(
      '[useViewables] viewable-folders:',
      folders.length,
      'geometry:',
      out.length
    );
  return out;
};

/** Build the list of viewables in bubble order and ensure defaultGeometry is first. */
export const collectViewables = (
  doc: Autodesk.Viewing.Document,
  debug = false
): ViewableInfo[] => {
  const root = doc.getRoot() as any;

  // 1) try BFS
  let items = gatherGeometryNodes(root, debug);

  // 2) fallback: “viewable” folders
  if (!items.length) items = gatherFromViewableFolders(root, debug);

  // 3) normalize
  let out: ViewableInfo[] = items.map((n: any, idx: number) => ({
    guid: normGuid(n, `__auto_${idx}`),
    name: normName(n, idx),
    role: normRole(n),
    node: n,
  }));

  // 4) put default geometry first (and inject if missing)
  const getDefault =
    typeof root.getDefaultGeometry === 'function'
      ? root.getDefaultGeometry.bind(root)
      : null;
  if (getDefault) {
    const def = getDefault();
    if (def) {
      const dGuid = normGuid(def, '__def');
      const dName = normName(def, 0);
      const dRole = normRole(def);
      const i = out.findIndex((v) => v.guid === dGuid);
      if (i >= 0) {
        const [d] = out.splice(i, 1);
        out.unshift(d);
      } else {
        out.unshift({ guid: dGuid, name: dName, role: dRole, node: def });
      }
    }
  } else if (!out.length) {
    // final fallback if absolutely nothing was found
    const def = (root as any)?.getDefaultGeometry?.();
    if (def) {
      out = [
        {
          guid: normGuid(def, '__def'),
          name: normName(def, 0),
          role: normRole(def),
          node: def,
        },
      ];
    }
  }

  // uniq by guid (sometimes duplicated in bubble)
  const uniq = Array.from(new Map(out.map((v) => [v.guid, v])).values());
  if (debug) console.log('[useViewables] collectViewables ->', uniq);
  return uniq;
};

/** Find a viewable node by guid inside an already-built list. */
export const findViewable = (list: ViewableInfo[], guid: string) =>
  list.find((v) => v.guid === guid);

/** Get initial node to load (default geometry or first of the provided list). */
export const getInitialNode = (
  doc: Autodesk.Viewing.Document,
  list?: ViewableInfo[]
): any => {
  const root = doc.getRoot() as any;
  const def =
    typeof root.getDefaultGeometry === 'function'
      ? root.getDefaultGeometry()
      : undefined;
  return def ?? list?.[0]?.node ?? undefined;
};

/** Load a viewable by guid; returns true if a node was found and load started. */
export const loadViewableByGuid = async (
  viewer: Autodesk.Viewing.GuiViewer3D,
  doc: Autodesk.Viewing.Document,
  list: ViewableInfo[],
  guid: string
): Promise<boolean> => {
  const v = findViewable(list, guid);
  if (!v) {
    console.warn('[useViewables] guid not found:', guid);
    return false;
  }
  await viewer.loadDocumentNode(doc, v.node, { keepCurrentModels: false });
  return true;
};
